import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface BadgeListProps {
  items: ReadonlyArray<string>;
  /** Cap on inline chips; the rest collapse into a "+N" popover. */
  max?: number;
  emptyLabel?: string;
  variant?: 'default' | 'secondary' | 'outline';
}

/**
 * Compact horizontal list of label chips with overflow → click popover.
 * Used by the user list for Locations / Groups / Modules — capped to
 * `max` inline chips; surplus items live behind a clickable "+N" badge
 * that opens a scrollable popover with a Copy-all action.
 *
 * Why a click popover (Radix) and not the tooltip we had before:
 *   - Persists on click — easier to read long lists.
 *   - Works on touch devices.
 *   - Lets us add affordances (Copy) inside the floating panel.
 */
export function BadgeList({
  items,
  max = 1,
  emptyLabel = '—',
  variant = 'secondary',
}: BadgeListProps) {
  if (!items || items.length === 0) {
    return <span className="text-muted-foreground">{emptyLabel}</span>;
  }
  const visible = items.slice(0, max);
  const overflow = items.slice(max);

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((item, i) => (
        <Badge
          key={`${item}-${i}`}
          variant={variant}
          className="max-w-[180px] truncate"
        >
          {item}
        </Badge>
      ))}
      {overflow.length > 0 && <OverflowPopover overflow={overflow} all={items} />}
    </div>
  );
}

function OverflowPopover({
  overflow,
  all,
}: {
  overflow: ReadonlyArray<string>;
  all: ReadonlyArray<string>;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(all.join(', '));
      setCopied(true);
      toast.success('Copied to clipboard');
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-5 cursor-pointer items-center rounded-md border bg-transparent px-2 py-0 text-xs font-medium text-foreground/80 transition-colors hover:border-ring hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label={`Show ${overflow.length} more`}
        >
          +{overflow.length}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
            {all.length} total
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={copy}
            className="-mr-1 h-6 px-2 text-xs"
          >
            {copied ? (
              <Check className="h-3 w-3" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
            {copied ? 'Copied' : 'Copy all'}
          </Button>
        </div>
        <div className="max-h-60 overflow-y-auto px-3 py-2">
          <ul className="flex flex-col gap-0.5 text-sm">
            {all.map((item, i) => (
              <li key={`${item}-${i}`} className="truncate" title={item}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
}
